import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import * as client from "../../../client";
import { ROUTES } from "../../../routes";
import { usePolicyApplications, useCreatePolicyApplications } from "./data";
import { statusSeverity, statusLabel, readableLayer } from "./util";
import styles from "./PoliciesTab.module.css";

export const PoliciesTab: React.FC<{
  specialtyPropertyInfo: client.ReportResponse;
}> = ({ specialtyPropertyInfo }) => {
  const reportId = specialtyPropertyInfo.report_id;
  const navigate = useNavigate();
  const { data: applications, isLoading } = usePolicyApplications(reportId);
  const create = useCreatePolicyApplications(reportId);

  const layersById = new Map(
    specialtyPropertyInfo.report_json.layer_info.map((layer) => [
      layer.layer_id,
      layer,
    ])
  );

  const open = (applicationId: string) =>
    navigate(ROUTES.DASHBOARD_POLICY_APPLICATION(applicationId));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Policy Applications</h2>
          <p className={styles.subtitle}>
            Each application is a policy spawned from a layer on this
            submission. Terms are pulled live from the submission and can be
            overridden per policy.
          </p>
        </div>
        <Button
          label="Sync from layers"
          icon="pi pi-sync"
          size="small"
          loading={create.isPending}
          onClick={() => create.mutate()}
        />
      </div>

      {isLoading ? (
        <div className={styles.centered}>
          <ProgressSpinner style={{ width: 40, height: 40 }} />
        </div>
      ) : !applications || applications.length === 0 ? (
        <div className={styles.empty}>
          <i className="pi pi-file-o" />
          <p>No policy applications yet.</p>
          <Button
            label="Create from layers"
            icon="pi pi-plus"
            loading={create.isPending}
            onClick={() => create.mutate()}
          />
        </div>
      ) : (
        <div className={styles.list}>
          {applications.map((app) => {
            const layer = layersById.get(app.layer_id);
            return (
              <button
                key={app.id}
                className={styles.row}
                onClick={() => open(app.id)}
              >
                <span className={styles.suffix}>{app.suffix}</span>
                <div className={styles.rowMain}>
                  <div className={styles.rowTitle}>
                    Policy {app.suffix}
                    {app.policy_number ? ` · ${app.policy_number}` : ""}
                  </div>
                  <div className={styles.rowMeta}>
                    {layer ? readableLayer(layer) : "Layer not found"}
                  </div>
                </div>
                <Tag
                  value={statusLabel(app.status)}
                  severity={statusSeverity(app.status)}
                  rounded
                />
                <i className={`pi pi-chevron-right ${styles.chevron}`} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
